/**
 * Boostly Script Blocking/Deactivation Logic
 * This file handles the immediate blocking of third-party scripts
 * Should be loaded as render-blocking to ensure scripts are caught early
 * License: shoplab GmbH non-commercial use only non-publishable
 * Author: Mark Chang <mark@shoplab.cc> 
 *
 */

(function boostlyScriptBlocker() {
  // CRITICAL FAST PATH: Use pre-compiled patterns from Liquid (server-side)
  // This avoids regex compilation overhead in the browser
  const boostlyConfig = window.BoostlyConfig || {};
  // Ensure rules is always a valid array (safe fallback when no metaobject is selected)
  const quickRules = Array.isArray(boostlyConfig.rules) ? boostlyConfig.rules : [];
  
  // Pre-compiled patterns array from Liquid (already expanded)
  const precompiledPatterns = boostlyConfig.allPatterns || [];

  // OPTIMIZATION: Use indexOf for fast pattern matching (faster than regex)
  const quickMatchesAnyPattern = (url) => {
    if (!url || precompiledPatterns.length === 0) return false;
    const lowerUrl = url.toLowerCase();
    for (let i = 0; i < precompiledPatterns.length; i++) {
      if (lowerUrl.indexOf(precompiledPatterns[i].toLowerCase()) !== -1) {
        return true;
      }
    }
    return false;
  };

  // Fallback: expand pattern for path variants (used only for per-rule matching)
  const expandPattern = (value) => {
    const variants = [];
    if (!value) return variants;
    const str = String(value).trim();
    if (!str) return variants;

    variants.push(str);

    if (/^https?:\/\//i.test(str)) {
      const withoutProtocol = str.replace(/^https?:\/\//i, '');
      const firstSlash = withoutProtocol.indexOf('/');
      if (firstSlash !== -1) {
        const pathOnly = withoutProtocol.slice(firstSlash);
        if (pathOnly && pathOnly.length > 1 && !variants.includes(pathOnly)) {
          variants.push(pathOnly);
        } 
      }
    }

    return variants;
  };

  const existingScripts = document.querySelectorAll('script[src]');
  const tempBlockedScripts = [];

  for (let i = 0; i < existingScripts.length; i += 1) {
    const script = existingScripts[i];
    // OPTIMIZATION: Use dataset for faster attribute check
    if (!(script.type === 'app/boostly' || script.dataset.boostlyIgnore)) {
      const {src} = script;
      // OPTIMIZATION: Use indexOf-based matching (faster than regex)
      if (src && quickMatchesAnyPattern(src)) {
        script.type = 'app/boostly';
        script.removeAttribute('src');
        tempBlockedScripts.push({script, src});
      }
    }
  }

  /**
   * Configuration object full setup with all the features
   * @type {Object}
   */
  const LOG_ENABLED = window.location.search.includes('boostly_log=1');
  const CONFIG = {
    logEnabled: LOG_ENABLED,
    maxPatterns: 1000,
    maxScriptSize: 10 * 1024 * 1024,
  };

  /**
   * Log helper - completely no-op when disabled (zero overhead)
   * @type {Object}
   */
  // eslint-disable-next-line no-console
  const noop = () => {};
  const Log = LOG_ENABLED
    ? {
        log: (...args) => console.log('[Boostly]', ...args),
        warn: (...args) => console.warn('[Boostly]', ...args),
        group: (...args) => console.group('[Boostly]', ...args),
        groupEnd: () => console.groupEnd(),
        table: (data) => data && console.table(data),
      }
    : {log: noop, warn: noop, group: noop, groupEnd: noop, table: noop};

  // Track URLs already processed to prevent duplicate work
  const processedUrls = new Set();
  const blockedUrls = new Set();

  // Global deduplication: track ALL script URLs that have been added to the DOM
  // This prevents duplicate script loading from theme sections
  const loadedScriptUrls = new Set();

  // Normalize URL helper (defined early for init use)
  const initNormalizeUrl = (url) => {
    if (!url) return '';
    try {
      return new URL(url, window.location.href).href;
    } catch (e) {
      return url;
    }
  };

  // Initialize with scripts already in the document
  // CRITICAL FIX: Only track scripts that MATCH Boostly's patterns
  // Other scripts should not be tracked to avoid blocking them later
  document.querySelectorAll('script[src]').forEach((s) => {
    const src = s.src || s.getAttribute('src');
    if (!src) return;
    // Only track if it matches our blocking patterns
    if (quickMatchesAnyPattern(src)) {
      const normalizedSrc = initNormalizeUrl(src);
      loadedScriptUrls.add(normalizedSrc);
    }
  });

  // CRITICAL: Also scan scripts inside <template> tags (used by is-land, etc.)
  // Templates are inert but their content will be moved to DOM later
  // CRITICAL FIX: Only track scripts that MATCH Boostly's patterns
  const scanTemplateScripts = () => {
    document.querySelectorAll('template').forEach((template) => {
      if (template.content) {
        template.content.querySelectorAll('script[src]').forEach((s) => {
          const src = s.getAttribute('src');
          if (!src) return;
          // Only track if it matches our blocking patterns
          if (quickMatchesAnyPattern(src)) {
            const normalizedSrc = initNormalizeUrl(src);
            if (!loadedScriptUrls.has(normalizedSrc)) {
              loadedScriptUrls.add(normalizedSrc);
              if (LOG_ENABLED) Log.log('Pre-tracking template script:', src);
            }
          }
        });
      }
    });
  };

  // Scan templates now (if any exist in head)
  scanTemplateScripts();

  // IMPORTANT: Also scan when DOM is ready (templates in body won't exist yet)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanTemplateScripts);
  }

  const logBlockEvent = (label, url, ruleId, context) => {
    if (!LOG_ENABLED || !url || blockedUrls.has(url)) return;
    blockedUrls.add(url);
    Log.log(`${label} (${context}):`, url, '| Rule:', ruleId || 'unknown');
  };

  const DEFAULT_RULES = [];
  const config = window.BoostlyConfig || {};
  // Ensure rules is always a valid array (safe fallback when no metaobject is selected)
  const RULES = Array.isArray(config.rules) ? config.rules : DEFAULT_RULES;

  /**
   * Validate rules structure with security checks
   * @param {Array} rules
   * @returns {boolean}
   */
  const areRulesValid = (rules) => {
    try {
      if (!Array.isArray(rules)) return false;
      if (rules.length > CONFIG.maxPatterns) {
        Log.warn(`Too many rules: ${rules.length}, max allowed: ${CONFIG.maxPatterns}`);
        return false;
      }
      return rules.every(
        (rule) =>
          rule &&
          typeof rule === 'object' &&
          typeof rule.id === 'string' &&
          rule.id.length < 100 && // Security: prevent excessive IDs
          Array.isArray(rule.patterns) &&
          rule.patterns.length < 100 && // Security: limit patterns per rule
          rule.patterns.every((p) => typeof p === 'string' && p.length < 500) // Security: limit pattern length
      );
    } catch (e) {
      Log.warn('Rule validation error:', e);
      return false;
    }
  };

  if (!areRulesValid(RULES)) {
    Log.warn('Invalid rules configuration, using defaults');
    Object.assign(window.BoostlyConfig, {rules: DEFAULT_RULES});
  }

  // Early exit if no rules configured (optimization - no blocking needed)
  if (RULES.length === 0) {
    Log.log('No blocking rules configured - Boostly inactive');
    // Expose minimal API so activation script doesn't error
    window.boostlyBlocker = {
      blockedScripts: new Map(),
      enabledRules: new Set(),
      RULES: [],
      Log,
      blockingDisabled: true,
      originalCreateElement: document.createElement,
      originalAppendChild: Element.prototype.appendChild,
      originalInsertBefore: Element.prototype.insertBefore,
      originalSetAttribute: Element.prototype.setAttribute,
      loadedScriptUrls: new Set(),
      processedUrls: new Set(),
      restoreOriginalMethods: () => {},
      disableBlocking: (cb) => { if (cb) cb(); },
    };
    return; // Exit early, no blocking needed
  }

  // Map to store hijacked scripts and their original attributes
  const blockedScripts = new Map();

  // Track which rules have been enabled (and should no longer block)
  const enabledRules = new Set();

  // Track blocking order per activation strategy
  const blockingIndexCounters = {
    idle: 0,
    interaction: 0,
    domready: 0,
    timer: 0,
    event: 0,
  };

  // Store original DOM methods before overriding
  const originalCreateElement = document.createElement;
  const originalAppendChild = Element.prototype.appendChild;
  const originalInsertBefore = Element.prototype.insertBefore;
  const originalSetAttribute = Element.prototype.setAttribute;

  // Fail-safe error tracking: restore original methods if too many errors occur
  let overrideErrorCount = 0;
  const MAX_OVERRIDE_ERRORS = 3;

  /**
   * Handle errors in DOM overrides - restore original methods if errors persist
   * @param {string} methodName - Name of the method that errored
   * @param {Error} error - The error that occurred
   */
  const handleOverrideError = (methodName, error) => {
    overrideErrorCount++;
    Log.warn(`Error in ${methodName} override (${overrideErrorCount}/${MAX_OVERRIDE_ERRORS}):`, error.message);

    if (overrideErrorCount >= MAX_OVERRIDE_ERRORS) {
      Log.warn('Too many DOM override errors - restoring original methods to prevent site breakage');
      // Restore all original DOM methods
      document.createElement = originalCreateElement;
      Element.prototype.appendChild = originalAppendChild;
      Element.prototype.insertBefore = originalInsertBefore;
      Element.prototype.setAttribute = originalSetAttribute;
      // Mark blocking as disabled
      if (window.boostlyBlocker) {
        window.boostlyBlocker.blockingDisabled = true;
      }
    }
  };

  // Fast regex escape function - optimized for speed
  const esc = (s) => {
    // No try-catch for speed, just ensure string
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  /**
   * Escape string for safe use in HTML attributes
   * Prevents XSS and attribute breaking from quotes/special chars
   * @param {string} str
   * @returns {string}
   */
  const escapeAttr = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\//g, '&#x2F;');
  };

  /**
   * Extract custom data-* attributes from an element (excluding Boostly's own)
   * @param {Element} element
   * @returns {Object} - Map of attribute names to values
   */
  const getCustomDataAttributes = (element) => {
    const attrs = {};
    if (!element || !element.attributes) return attrs;
    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      if (attr.name.startsWith('data-') && !attr.name.startsWith('data-boostly')) {
        attrs[attr.name] = attr.value;
      }
    }
    return attrs;
  };

  // Pre-compile all patterns into a single regex for initial blocking (FAST)
  // We'll build individual rule regexes later for matching
  const allPatterns = [];
  const ruleRegexCache = new WeakMap();

  // Collect all patterns first
  for (let i = 0; i < RULES.length; i += 1) {
    const rule = RULES[i];
    if (rule.patterns && rule.patterns.length) {
      const expanded = rule.patterns.flatMap(expandPattern).filter(Boolean);
      if (expanded.length) {
        allPatterns.push(...expanded);
        try {
          const regex = new RegExp(expanded.map(esc).join('|'), 'i');
          ruleRegexCache.set(rule, regex);
        } catch (e) {
          // Silent fail for speed
        }
      }
    }
  }

  /**
   * Build a dynamic regex that excludes enabled rules with caching
   * @returns {RegExp|null}
   */
  let cachedRegex = null;
  let cachedRulesState = '';

  const getActiveBlockingRegex = () => {
    try {
      const currentState = Array.from(enabledRules).sort().join(',');
      if (currentState === cachedRulesState && cachedRegex) {
        return cachedRegex;
      }

      const activeRules = RULES.filter((r) => !enabledRules.has(r.id));
      const activePatterns = activeRules.flatMap((r) =>
        (r.patterns || []).flatMap(expandPattern).filter(Boolean)
      );

      if (activePatterns.length === 0) {
        cachedRegex = null;
        cachedRulesState = currentState;
        return null;
      }

      cachedRegex = new RegExp(activePatterns.map(esc).join('|'), 'i');
      cachedRulesState = currentState;
      return cachedRegex;
    } catch (e) {
      Log.warn('Failed to build blocking regex:', e);
      return null;
    }
  };

  // Initial regex with all patterns
  let ALL_REGEX = getActiveBlockingRegex();

  // Cache for checkScript results - prevents duplicate checks
  const urlCheckCache = new Map();

  /**
   * Check if a URL matches our blocking patterns and find the matching rule
   * Returns { shouldBlock: boolean, rule: object|null } for efficiency
   * Uses caching to prevent duplicate checks on the same URL
   * OPTIMIZATION: Uses indexOf for fast pattern matching
   * @param {string} url
   * @returns {{ shouldBlock: boolean, rule: object|null }}
   */
  const checkScript = (url) => {
    if (!url || typeof url !== 'string') return {shouldBlock: false, rule: null};
    if (url.indexOf('boostly') !== -1) return {shouldBlock: false, rule: null};

    // Check cache first - O(1) lookup
    const cached = urlCheckCache.get(url);
    if (cached !== undefined) return cached;

    // OPTIMIZATION: Quick rejection using indexOf (faster than regex)
    if (!quickMatchesAnyPattern(url)) {
      const result = {shouldBlock: false, rule: null};
      urlCheckCache.set(url, result);
      return result;
    }

    // Find matching rule using indexOf (faster than regex)
    const lowerUrl = url.toLowerCase();
    const rule = RULES.find((r) => {
      if (!r.patterns || !r.patterns.length) return false;
      return r.patterns.some((p) => lowerUrl.indexOf(p.toLowerCase()) !== -1);
    });

    const result = {shouldBlock: true, rule};
    urlCheckCache.set(url, result);
    return result;
  };

  // Backward compatibility wrapper
  const shouldBlockScript = (url) => checkScript(url).shouldBlock;

  /**
   * Check if a script element already has Boostly attributes configured
   * OPTIMIZATION: Uses dataset for faster attribute access
   * @param {Element} element
   * @returns {boolean}
   */
  const hasBoostlyAttributes = (element) => {
    return element && (element.type === 'app/boostly' || element.dataset.activation);
  };

  /**
   * Override document.createElement to hijack script creation
   * Optimized: removed expensive stack trace detection
   * Wrapped in try/catch for fail-safe operation
   * @param {string} tagName
   * @returns {Element}
   */
  document.createElement = function createElementHijack(tagName) {
    // Always create the element first using original method
    const element = originalCreateElement.call(this, tagName);

    try {
      if (tagName.toLowerCase() === 'script') {
        // OPTIMIZATION: Use dataset for faster attribute checks
        if (element.dataset.boostlyIgnore || hasBoostlyAttributes(element)) {
          return element;
        }

        // Override the src setter to intercept script sources
        const descriptor = Object.getOwnPropertyDescriptor(element, 'src') || {
          get() {
            return element.getAttribute('src');
          },
          set(value) {
            try {
              // OPTIMIZATION: Use dataset for faster attribute checks
              if (
                window.boostlyBlocker?.blockingDisabled ||
                element.dataset.boostlyAllowed ||
                element.dataset.boostlyHijacked ||
                processedUrls.has(value)
              ) {
                originalSetAttribute.call(element, 'src', value);
                return;
              }

              // Only apply deduplication to scripts that match Boostly rules
              // Scripts that don't match PASS THROUGH UNTOUCHED
              if (!quickMatchesAnyPattern(value)) {
                originalSetAttribute.call(element, 'src', value);
                return;
              }

              // Only for scripts matching our patterns: Check for duplicate URLs
              const normalizedValue = initNormalizeUrl(value);
              if (loadedScriptUrls.has(normalizedValue)) {
                if (LOG_ENABLED) Log.log('Preventing duplicate script load (createElement):', value);
                // Don't set src - this prevents the network request entirely
                return;
              }

              // Use optimized checkScript with caching
              const {shouldBlock, rule: matchedRule} = checkScript(value);

              if (shouldBlock && !element.dataset.boostlyAllowed) {
                processedUrls.add(value);
                loadedScriptUrls.add(normalizedValue);

                // Store original script info
                blockedScripts.set(element, {
                  src: value,
                  originalType: element.type || 'text/javascript',
                  async: element.async,
                  defer: element.defer,
                });

                // Change type to prevent execution
                element.type = 'app/boostly';
                originalSetAttribute.call(element, 'data-src', escapeAttr(value));
                originalSetAttribute.call(element, 'data-boostly-hijacked', 'true');

                if (matchedRule) {
                  originalSetAttribute.call(element, 'data-id', escapeAttr(matchedRule.id));
                  const activationStrategy = matchedRule.activation?.strategy || 'idle';
                  originalSetAttribute.call(element, 'data-activation', escapeAttr(activationStrategy));

                  if (blockingIndexCounters[activationStrategy] !== undefined) {
                    blockingIndexCounters[activationStrategy] += 1;
                    originalSetAttribute.call(
                      element,
                      'data-priority',
                      String(blockingIndexCounters[activationStrategy])
                    );
                  }
                }

                logBlockEvent('Script blocked', value, matchedRule?.id, 'createElement');
              } else {
                // Track for global deduplication
                loadedScriptUrls.add(normalizedValue);
                originalSetAttribute.call(element, 'src', value);
              }
            } catch (setterError) {
              // Fail-safe: if setter fails, just set src normally
              handleOverrideError('createElement.src.set', setterError);
              originalSetAttribute.call(element, 'src', value);
            }
          },
        };

        Object.defineProperty(element, 'src', {
          get: descriptor.get,
          set: descriptor.set,
          configurable: true,
        });
      }
    } catch (error) {
      handleOverrideError('createElement', error);
    }

    return element;
  };

  /**
   * Process and hijack a script element
   * Optimized: uses checkScript for single-pass matching + URL tracking
   * @param {Element} script
   * @param {Object} [preMatchedRule] - Optional pre-matched rule to avoid re-checking
   * @returns {void}
   */
  const hijackScript = (script, preMatchedRule = null) => {
    // OPTIMIZATION: Use dataset for faster attribute checks
    if (
      !script ||
      !script.dataset ||
      script.dataset.boostlyHijacked ||
      script.dataset.boostlyAllowed ||
      script.dataset.boostlyIgnore ||
      hasBoostlyAttributes(script)
    ) {
      return;
    }

    const src = script.src || script.getAttribute('src');
    if (!src) return;

    // Skip if this URL was already processed (prevents duplicates)
    if (processedUrls.has(src)) return;
    processedUrls.add(src);

    // Use pre-matched rule if provided, otherwise check
    const matchedRule = preMatchedRule || checkScript(src).rule;
    if (!matchedRule && !preMatchedRule) return; // Not a blocking match

    // Store original script info (preserve all attributes for rehydration)
    blockedScripts.set(script, {
      src,
      originalType: script.type || 'text/javascript',
      async: script.async,
      defer: script.defer,
      blocking: script.getAttribute('blocking'),
      fetchpriority: script.getAttribute('fetchpriority'),
      // Security and identity attributes
      nonce: script.nonce || script.getAttribute('nonce'),
      integrity: script.getAttribute('integrity'),
      referrerpolicy: script.getAttribute('referrerpolicy'),
      crossorigin: script.getAttribute('crossorigin'),
      id: script.id || script.getAttribute('id'),
      // Custom data-* attributes (excluding Boostly's own)
      customDataAttrs: getCustomDataAttributes(script),
    });

    // Remove attributes to prevent loading
    script.removeAttribute('src');

    // Mark as hijacked and store original data
    script.setAttribute('data-boostly-hijacked', 'true');
    script.setAttribute('data-src', escapeAttr(src));
    script.setAttribute('type', 'app/boostly');

    if (matchedRule) {
      script.setAttribute('data-id', escapeAttr(matchedRule.id));
      const activationStrategy = matchedRule.activation?.strategy || 'idle';
      script.setAttribute('data-activation', escapeAttr(activationStrategy));

      if (blockingIndexCounters[activationStrategy] !== undefined) {
        blockingIndexCounters[activationStrategy] += 1;
        script.setAttribute('data-priority', String(blockingIndexCounters[activationStrategy]));
      }
    }

    logBlockEvent('Script blocked', src, matchedRule?.id, 'dom');
  };

  // Note: We only block external scripts (those with src attribute)
  // Inline scripts execute immediately when parsed, so blocking them has no effect

  /**
   * Helper to check and hijack script in DOM methods
   * @param {Element} node
   */
  const checkAndHijack = (node) => {
    if (!node || !node.dataset) return;

    // OPTIMIZATION: Use dataset for faster attribute checks
    if (
      node.tagName === 'SCRIPT' &&
      !node.dataset.boostlyAllowed &&
      !node.dataset.boostlyIgnore &&
      !node.dataset.boostlyHijacked &&
      !hasBoostlyAttributes(node)
    ) {
      const src = node.src || node.getAttribute('src');
      if (src && !processedUrls.has(src)) {
        const {shouldBlock, rule} = checkScript(src);
        if (shouldBlock) hijackScript(node, rule);
      }
    }

    // Check preloads (fonts, scripts)
    if (
      node.tagName === 'LINK' &&
      node.rel === 'preload' &&
      !node.dataset.boostlyBlocked &&
      !node.dataset.boostlyAllowed &&
      !node.dataset.boostlyIgnore
    ) {
      const href = node.href || node.getAttribute('href');
      // Skip if no href or URL already processed
      if (href && !processedUrls.has(href)) {
        const {shouldBlock, rule} = checkScript(href);
        if (shouldBlock) {
          processedUrls.add(href);
          // Preserve additional preload attributes for rehydration
          const originalAs = node.getAttribute('as') || '';
          const originalCrossorigin = node.getAttribute('crossorigin');
          const originalIntegrity = node.getAttribute('integrity');
          const originalReferrerpolicy = node.getAttribute('referrerpolicy');
          const originalId = node.id || node.getAttribute('id');
          const originalType = node.getAttribute('type');

          node.removeAttribute('href');
          node.setAttribute('data-href', escapeAttr(href));
          node.setAttribute('data-boostly-blocked', 'true');
          node.setAttribute('data-as', originalAs);
          if (originalCrossorigin) node.setAttribute('data-crossorigin', originalCrossorigin);
          if (originalIntegrity) node.setAttribute('data-integrity', originalIntegrity);
          if (originalReferrerpolicy) node.setAttribute('data-referrerpolicy', originalReferrerpolicy);
          if (originalId) node.setAttribute('data-original-id', originalId);
          if (originalType) node.setAttribute('data-type', originalType);
          node.setAttribute('rel', 'boostly-preload');

          if (rule) {
            node.setAttribute('data-id', escapeAttr(rule.id));
            const activationStrategy = rule.activation?.strategy || 'idle';
            node.setAttribute('data-activation', escapeAttr(activationStrategy));

            if (blockingIndexCounters[activationStrategy] !== undefined) {
              blockingIndexCounters[activationStrategy] += 1;
              node.setAttribute('data-priority', String(blockingIndexCounters[activationStrategy]));
            }
          }

          logBlockEvent('Preload blocked', href, rule?.id, 'dom');
        }
      }
    }
  };

  /**
   * Normalize URL for comparison (handles relative vs absolute)
   * @param {string} url
   * @returns {string}
   */
  const normalizeUrl = (url) => {
    if (!url) return '';
    try {
      // Convert to absolute URL for consistent comparison
      return new URL(url, window.location.href).href;
    } catch (e) {
      return url;
    }
  };

  /**
   * Check if a script with this URL already exists in the DOM
   * @param {string} src
   * @returns {boolean}
   */
  const scriptExistsInDom = (src) => {
    if (!src) return false;
    const normalizedSrc = normalizeUrl(src);
    const scripts = document.querySelectorAll('script[src]');
    for (let i = 0; i < scripts.length; i++) {
      const existingSrc = scripts[i].src || scripts[i].getAttribute('src');
      if (normalizeUrl(existingSrc) === normalizedSrc) {
        return true;
      }
    }
    return false;
  };

  /**
   * Check if a script element is a duplicate and should be skipped
   * CRITICAL: Only deduplicate scripts that match Boostly's blocking rules
   * Other scripts should NEVER be touched - they pass through untouched
   * @param {Element} node
   * @returns {boolean} - true if duplicate (should skip)
   */
  const isDuplicateScript = (node) => {
    if (!node || node.tagName !== 'SCRIPT') return false;
    const src = node.src || node.getAttribute('src');
    if (!src) return false;

    // Only consider scripts that match our blocking patterns
    // Scripts that don't match Boostly rules PASS THROUGH UNTOUCHED
    if (!quickMatchesAnyPattern(src)) {
      return false;
    }

    const normalizedSrc = normalizeUrl(src);

    // Already tracked as loaded - skip (only for scripts matching our patterns)
    if (loadedScriptUrls.has(normalizedSrc)) {
      if (LOG_ENABLED) Log.log('Skipping duplicate script (tracked):', src);
      return true;
    }

    // Live DOM check - does this script already exist?
    if (scriptExistsInDom(src)) {
      loadedScriptUrls.add(normalizedSrc);
      if (LOG_ENABLED) Log.log('Skipping duplicate script (DOM exists):', src);
      return true;
    }

    // Track this URL (only for scripts matching our patterns)
    loadedScriptUrls.add(normalizedSrc);
    return false;
  };

  /**
   * Override appendChild - main method for script insertion
   * Wrapped in try/catch for fail-safe operation
   */
  Element.prototype.appendChild = function appendChildOverride(child) {
    try {
      // Skip duplicate scripts - ONLY for Boostly-matched scripts
      if (isDuplicateScript(child)) {
        return child; // Return the child without appending
      }
      checkAndHijack(child);
    } catch (error) {
      handleOverrideError('appendChild', error);
    }
    return originalAppendChild.call(this, child);
  };

  /**
   * Override insertBefore - common for script loaders
   * Wrapped in try/catch for fail-safe operation
   */
  Element.prototype.insertBefore = function insertBeforeOverride(newNode, referenceNode) {
    try {
      // Skip duplicate scripts (theme deduplication)
      if (isDuplicateScript(newNode)) {
        return newNode; // Return the node without inserting
      }
      checkAndHijack(newNode);
    } catch (error) {
      handleOverrideError('insertBefore', error);
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };

  // Note: replaceChild is rarely used for scripts, removed for performance

  /**
   * Override setAttribute to catch src changes
   * Wrapped in try/catch for fail-safe operation
   * @param {string} name
   * @param {string} value
   * @returns {void}
   */
  Element.prototype.setAttribute = function setAttributeOverride(name, value) {
    try {
      // OPTIMIZATION: Use dataset for faster attribute checks
      if (
        this.tagName === 'SCRIPT' &&
        name === 'src' &&
        value &&
        !processedUrls.has(value) &&
        !this.dataset?.boostlyAllowed &&
        !this.dataset?.boostlyIgnore &&
        !hasBoostlyAttributes(this) &&
        shouldBlockScript(value)
      ) {
        hijackScript(this);
        return; // Don't call original setAttribute - hijackScript handles it
      }
    } catch (error) {
      handleOverrideError('setAttribute', error);
    }
    originalSetAttribute.call(this, name, value);
  };

  // Note: innerHTML and document.write overrides REMOVED
  // - Scripts added via innerHTML don't execute anyway
  // - document.write is deprecated and never used by modern scripts
  // The essential overrides (createElement, appendChild, insertBefore, setAttribute) catch all real cases.

  /**
   * Process existing scripts (skip already processed URLs)
   * @returns {void}
   */
  const processExistingScripts = () => {
    const scripts = document.querySelectorAll('script[src]');
    scripts.forEach((script) => {
      const src = script.src || script.getAttribute('src');
      // Skip if already processed or not blocking
      if (src && !processedUrls.has(src) && shouldBlockScript(src)) {
        hijackScript(script);
      }
    });
  };

  // Process the scripts we fast-blocked at the start
  // Now add proper attributes and metadata
  if (tempBlockedScripts.length > 0) {
    Log.log(`Fast-blocked ${tempBlockedScripts.length} script(s), adding metadata`);
  }

  tempBlockedScripts.forEach(({script, src}) => {
    // Track this URL as processed
    processedUrls.add(src);

    // Special handling for data-cfasync scripts (Cloudflare Rocket Loader)
    if (script.hasAttribute('data-cfasync')) {
      script.removeAttribute('data-cfasync');
    }

    // Store original attributes before modification (preserve all for rehydration)
    const scriptInfo = {
      src,
      originalType: 'text/javascript',
      async: script.async,
      defer: script.defer,
      blocking: script.getAttribute('blocking'),
      fetchpriority: script.getAttribute('fetchpriority'),
      // Security and identity attributes
      nonce: script.nonce || script.getAttribute('nonce'),
      integrity: script.getAttribute('integrity'),
      referrerpolicy: script.getAttribute('referrerpolicy'),
      crossorigin: script.getAttribute('crossorigin'),
      id: script.id || script.getAttribute('id'),
      // Custom data-* attributes (excluding Boostly's own)
      customDataAttrs: getCustomDataAttributes(script),
      isInline: false,
    };
    blockedScripts.set(script, scriptInfo);

    // Add proper attributes (already has type="app/boostly" and no src)
    originalSetAttribute.call(script, 'data-src', escapeAttr(src));
    originalSetAttribute.call(script, 'data-boostly-hijacked', 'true');

    // Store other attributes as data-* attributes
    if (script.async) originalSetAttribute.call(script, 'data-async', 'true');
    if (script.defer) originalSetAttribute.call(script, 'data-defer', 'true');
    if (script.getAttribute('blocking')) {
      originalSetAttribute.call(script, 'data-blocking', script.getAttribute('blocking'));
    }
    if (script.getAttribute('fetchpriority')) {
      originalSetAttribute.call(script, 'data-fetchpriority', script.getAttribute('fetchpriority'));
    }

    // Find matching rule (use cached result if available)
    const cached = urlCheckCache.get(src);
    const matchingRule = cached?.rule || RULES.find((rule) => {
      const regex = ruleRegexCache.get(rule);
      return regex && regex.test(src);
    });

    if (matchingRule) {
      originalSetAttribute.call(script, 'data-id', escapeAttr(matchingRule.id));
      const activationStrategy = matchingRule.activation?.strategy || 'idle';
      originalSetAttribute.call(script, 'data-activation', escapeAttr(activationStrategy));

      if (blockingIndexCounters[activationStrategy] !== undefined) {
        blockingIndexCounters[activationStrategy] += 1;
        originalSetAttribute.call(script, 'data-priority', String(blockingIndexCounters[activationStrategy]));
      }
    }

    logBlockEvent('Script blocked', src, matchingRule?.id, 'fast-path');
  });

  // Fast-path preload blocking (fonts, etc.)
  const existingPreloads = document.querySelectorAll('link[rel="preload"][href]');
  let preloadBlockedCount = 0;

  existingPreloads.forEach((link) => {
    // OPTIMIZATION: Use dataset for faster attribute check
    if (link.dataset.boostlyIgnore) return;
    const href = link.href || link.getAttribute('href');
    // Skip if already processed
    if (!href || processedUrls.has(href)) return;
    // OPTIMIZATION: Use indexOf-based matching (faster than regex)
    if (quickMatchesAnyPattern(href)) {
      processedUrls.add(href);
      const originalAs = link.getAttribute('as') || '';
      // Preserve additional preload attributes for rehydration
      const originalCrossorigin = link.getAttribute('crossorigin');
      const originalIntegrity = link.getAttribute('integrity');
      const originalReferrerpolicy = link.getAttribute('referrerpolicy');
      const originalId = link.id || link.getAttribute('id');
      const originalType = link.getAttribute('type');

      link.removeAttribute('href');
      originalSetAttribute.call(link, 'data-href', escapeAttr(href));
      originalSetAttribute.call(link, 'data-boostly-blocked', 'true');
      originalSetAttribute.call(link, 'data-as', originalAs);
      if (originalCrossorigin) originalSetAttribute.call(link, 'data-crossorigin', originalCrossorigin);
      if (originalIntegrity) originalSetAttribute.call(link, 'data-integrity', originalIntegrity);
      if (originalReferrerpolicy) originalSetAttribute.call(link, 'data-referrerpolicy', originalReferrerpolicy);
      if (originalId) originalSetAttribute.call(link, 'data-original-id', originalId);
      if (originalType) originalSetAttribute.call(link, 'data-type', originalType);
      originalSetAttribute.call(link, 'rel', 'boostly-preload');

      // Find matching rule (use cached result if available)
      const cached = urlCheckCache.get(href);
      const matchingRule = cached?.rule || RULES.find((rule) => {
        const regex = ruleRegexCache.get(rule);
        return regex && regex.test(href);
      });

      if (matchingRule) {
        originalSetAttribute.call(link, 'data-id', escapeAttr(matchingRule.id));
        const activationStrategy = matchingRule.activation?.strategy || 'idle';
        originalSetAttribute.call(link, 'data-activation', escapeAttr(activationStrategy));

        if (blockingIndexCounters[activationStrategy] !== undefined) {
          blockingIndexCounters[activationStrategy] += 1;
          originalSetAttribute.call(link, 'data-priority', String(blockingIndexCounters[activationStrategy]));
        }
      }

      logBlockEvent('Preload blocked', href, matchingRule?.id, 'fast-path');
      preloadBlockedCount += 1;
    }
  });

  if (preloadBlockedCount > 0) {
    Log.log(`Fast-blocked ${preloadBlockedCount} preload(s)`);
  }

  // Process existing scripts when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processExistingScripts);
  } else {
    processExistingScripts();
  }

  // Additional check for scripts that might be missed
  // This runs after a micro-task to catch scripts added synchronously after our initial scan
  Promise.resolve().then(() => {
    const missedScripts = document.querySelectorAll('script[src]:not([type="app/boostly"])');
    missedScripts.forEach((script) => {
      const src = script.src || script.getAttribute('src');
      // OPTIMIZATION: Use indexOf-based matching (faster than regex)
      if (src && !processedUrls.has(src) && quickMatchesAnyPattern(src) && !hasBoostlyAttributes(script)) {
        hijackScript(script);
      }
    });
  });

  /**
   * Expose necessary functions and data to the activation script
   * @type {Object}
   */
  // Track blocking and activation state to prevent race conditions
  const blockingState = {
    isInitializing: true,
    isProcessing: false,
    scriptsBeingProcessed: 0,
    isFullyDisabled: false, // Track when blocking is completely disabled
  };

  const activationState = {
    isActivating: false,
    pendingScripts: new Set(),
  };

  window.boostlyBlocker = {
    blockedScripts,
    enabledRules,
    RULES,
    Log,
    getActiveBlockingRegex,
    ALL_REGEX,
    loadedScriptUrls, // Exposed for activation script to update
    processedUrls, // Exposed for activation script to check
    updateRegex() {
      ALL_REGEX = getActiveBlockingRegex();
    },
    blockingState,
    activationState,
    originalCreateElement,
    originalAppendChild,
    originalInsertBefore,
    originalSetAttribute,
    blockingDisabled: false, // Flag to track if blocking is fully disabled
    restoreOriginalMethods() {
      document.createElement = originalCreateElement;
      Element.prototype.appendChild = originalAppendChild;
      Element.prototype.insertBefore = originalInsertBefore;
      Element.prototype.setAttribute = originalSetAttribute;
      Log.log('DOM methods restored');
    },
    disableBlocking(callback) {
      // Prevent multiple calls
      if (blockingState.isFullyDisabled) {
        Log.log('Blocking already disabled, skipping');
        if (callback) callback();
        return;
      }

      Log.log('Disabling all blocking mechanisms');

      // Restore all original DOM methods
      window.boostlyBlocker.restoreOriginalMethods();

      // Mark that blocking is fully disabled
      window.boostlyBlocker.blockingDisabled = true;
      blockingState.isFullyDisabled = true;
      Log.log('Blocking fully disabled');

      if (callback) callback();
    },
  };

  // Log initial status (minimal output)
  Log.log('Boostly initialized');
  if (LOG_ENABLED) {
    Log.log(
      'Active rules:',
      RULES.map((r) => `${r.id} (${r.activation?.strategy || 'idle'})`).join(', ')
    );
    Log.group('Pattern details:');
    RULES.forEach((rule) => {
      Log.log(`Rule "${rule.id}":`, rule.patterns?.join(', ') || 'NO PATTERNS');
    });
    Log.groupEnd();
  }

  /**
   * Log a summary of all blocked scripts (for debugging)
   */
  const logBlockingSummary = () => {
    const blockedCount = blockedScripts.size;
    Log.log(`=== BLOCKING SUMMARY: ${blockedCount} script(s) blocked ===`);
    
    if (blockedCount > 0) {
      const byStrategy = {};
      blockedScripts.forEach((info, element) => {
        const strategy = element.getAttribute('data-activation') || 'idle';
        const ruleId = element.getAttribute('data-id') || 'unknown';
        if (!byStrategy[strategy]) byStrategy[strategy] = [];
        byStrategy[strategy].push({
          src: info.src,
          ruleId,
          priority: element.getAttribute('data-priority') || '0',
        });
      });
      
      Object.entries(byStrategy).forEach(([strategy, scripts]) => {
        Log.log(`Strategy "${strategy}": ${scripts.length} script(s)`);
        scripts.forEach((s) => {
          Log.log(`  - [${s.ruleId}] (priority ${s.priority}): ${s.src}`);
        });
      });
    }
    
    Log.log(`Total URLs tracked: ${loadedScriptUrls.size}`);
    Log.log('=== END BLOCKING SUMMARY ===');
  };

  /**
   * Dynamically inject the activation script AFTER blocking is complete
   * This ensures the activation script builds its snapshot with all blocked scripts
   */
  const injectActivationScript = () => {
    const activationUrl = window.BoostlyConfig?.activationScriptUrl;
    if (!activationUrl) {
      Log.warn('No activation script URL configured');
      return;
    }

    // Check if activation script is already loaded
    if (document.querySelector('script[src*="boostly-activation"]')) {
      Log.log('Activation script already loaded, skipping injection');
      return;
    }

    Log.log('Injecting activation script:', activationUrl);

    // Create and inject the activation script
    const script = originalCreateElement.call(document, 'script');
    originalSetAttribute.call(script, 'id', 'boostly-activation');
    originalSetAttribute.call(script, 'data-boostly-ignore', 'true');
    originalSetAttribute.call(script, 'type', 'module');
    originalSetAttribute.call(script, 'src', activationUrl);

    // Append to head
    document.head.appendChild(script);
    Log.log('Activation script injected successfully');
  };

  // Mark blocking complete flag
  let blockingComplete = false;

  /**
   * Called when blocking is fully complete
   */
  const onBlockingComplete = () => {
    if (blockingComplete) return;
    blockingComplete = true;

    blockingState.isInitializing = false;
    Log.log('=== BLOCKING PHASE COMPLETE ===');
    logBlockingSummary();

    // Now inject the activation script
    Log.log('Blocking complete - injecting activation script');
    injectActivationScript();
  };

  // Mark initialization complete after DOM settles
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onBlockingComplete);
  } else {
    // DOM already ready, run after a microtask to catch any remaining scripts
    setTimeout(onBlockingComplete, 50);
  }

})();